import { useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export interface ListPageStateOptions {
  basePath: string;
  defaultPageSize?: number;
}

export interface ListPageState {
  selectedId: string | undefined;
  page: number;
  pageSize: number;
  setPage: (page: number, size?: number, opts?: { replace?: boolean }) => void;
  openDrawer: (id: string) => void;
  closeDrawer: () => void;
  redirectToBucket: (bucketPath: string, id: string, locatedPage?: number) => void;
}

export function useListPageState(options: ListPageStateOptions): ListPageState {
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.max(1, Number(searchParams.get('pageSize')) || options.defaultPageSize || 10);

  const setPage = useCallback(
    (newPage: number, newSize?: number, opts?: { replace?: boolean }) => {
      const safePage = Math.max(1, Math.floor(newPage) || 1);
      const next = new URLSearchParams(searchParams);
      next.set('page', String(safePage));
      if (newSize && newSize > 0) next.set('pageSize', String(Math.floor(newSize)));
      setSearchParams(next, { replace: opts?.replace ?? false });
    },
    [searchParams, setSearchParams]
  );

  const openDrawer = useCallback(
    (recordId: string) => {
      const qs = searchParams.toString();
      navigate(`${options.basePath}/${recordId}${qs ? `?${qs}` : ''}`);
    },
    [searchParams, navigate, options.basePath]
  );

  const closeDrawer = useCallback(() => {
    const qs = searchParams.toString();
    navigate(`${options.basePath}${qs ? `?${qs}` : ''}`);
  }, [searchParams, navigate, options.basePath]);

  const redirectToBucket = useCallback(
    (bucketPath: string, recordId: string, locatedPage?: number) => {
      // Cross-bucket redirects discard the source pagination (each bucket has
      // its own page count) but preserve other filters like searchId. If the
      // caller knows the destination page (from the same locate response that
      // told us the bucket changed), carry it in the URL so the destination
      // tab doesn't have to re-locate.
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      next.delete('pageSize');
      if (locatedPage !== undefined) next.set('page', String(locatedPage));
      const qs = next.toString();
      navigate(`${bucketPath}/${recordId}${qs ? `?${qs}` : ''}`, { replace: true });
    },
    [searchParams, navigate]
  );

  return { selectedId: id, page, pageSize, setPage, openDrawer, closeDrawer, redirectToBucket };
}
